import React, { useState, useCallback, Suspense, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import DeliveryOverview from '@/components/deliver/DeliveryOverview';
import CommunicationHub from '@/components/communicate/CommunicationHub';
import DecisionBoard from '@/components/decide/DecisionBoard';
import TraceabilityView from '@/components/governance/TraceabilityView';
import TodayView from '@/components/today/TodayView';
import Dashboard from '@/components/dashboard/Dashboard';
import ExecutiveDashboard from '@/components/dashboard/ExecutiveDashboard';
import PMOCommandCenter from '@/components/dashboard/PMOCommandCenter';
import ControlCenter from '@/components/admin/ControlCenter';
import { homeFor } from '@/lib/roleHome';
import ExecutiveBrief from '@/components/home/ExecutiveBrief';
import ProjectCockpit from '@/components/home/ProjectCockpit';
import ProgramBoard from '@/components/home/ProgramBoard';
import { HomeSkeleton } from '@/components/home/HomeFrame';
import MyDay from '@/components/home/MyDay';
import WorkspaceHealth from '@/components/home/WorkspaceHealth';
import SmartInbox from '@/components/inbox/SmartInbox';
import ActionHub from '@/components/actions/ActionHub';
import MeetingHub from '@/components/meetings/MeetingHub';
import CalendarView from '@/components/calendar/CalendarView';
import StrategyView from '@/components/strategy/StrategyView';
import ProjectsView from '@/components/projects/ProjectsView';
import TeamView from '@/components/team/TeamView';
import DocumentsView from '@/components/documents/DocumentsView';
import DocumentIntelligencePanel from '@/components/documents/DocumentIntelligencePanel';
import StakeholderView from '@/components/stakeholders/StakeholderView';
import { ProjectDependenciesSection } from '@/components/projects/ProjectDependenciesSection';
import ActivityView from '@/components/activity/ActivityView';
import ReportsView from '@/components/reports/ReportsView';
import WeeklyDigestView from '@/components/digest/WeeklyDigestView';
import AIControlsView from '@/components/ai/AIControlsView';
import HITLGovernanceView from '@/components/governance/HITLGovernanceView';
import AuditTrailView from '@/components/governance/AuditTrailView';
import ActionsRequired from '@/components/governance/ActionsRequired';
import { SignalEngineView } from '@/components/signals/SignalEngineView';
import RiskPredictionView from '@/components/risk/RiskPredictionView';
import ReleaseIntelligenceView from '@/components/engineering/ReleaseIntelligenceView';
import DecisionLog from '@/components/decisions/DecisionLog';
import { IssueRegister } from '@/components/issues/IssueRegister';
import { KnowledgeBase } from '@/components/knowledge/KnowledgeBase';
import { BrandingView } from '@/components/branding/BrandingView';
import { PortfolioView } from '@/components/portfolio/PortfolioView';
import { ComplianceCenter } from '@/components/compliance/ComplianceCenter';
import ConsoleLayout from '@/components/layout/ConsoleLayout';
import SectionTabs from '@/components/layout/SectionTabs';
import { ProtectedFeature } from '@/components/auth/ProtectedFeature';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { useUserRole } from '@/hooks/useUserRole';
import { useLoginSummary } from '@/hooks/useLoginSummary';
import { useLocalization } from '@/hooks/useLocalization';

const TenantSettings = lazy(() => import('@/components/admin/TenantSettings'));
const AdminAnalytics = lazy(() => import('@/pages/AdminAnalytics'));

const Index = () => {
  const { t } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();
  // The active view lives in the URL (?view=<key>) so Back/Forward, refresh and
  // shared links all work, and the meeting-recorder extension can deep-link
  // into a view. Absent/unknown values fall back to the dashboard. Navigating
  // pushes a history entry; the default view omits the param.
  const activeView = searchParams.get('view') || 'dashboard';
  const setActiveView = useCallback((view: string, params?: Record<string, string>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (view === 'dashboard') next.delete('view');
        else next.set('view', view);
        // Deep-link params (e.g. project) belong to one navigation; never carry them to the next view.
        next.delete('project');
        Object.entries(params ?? {}).forEach(([k, v]) => next.set(k, v));
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);
  const { trackAction } = useActivityTracking();
  const { role, loading: roleLoading } = useUserRole();

  const isExecutive = role === 'executive';
  const home = homeFor(role);

  const handleViewChange = (view: string, params?: Record<string, string>) => {
    setActiveView(view, params);
    trackAction({ actionType: 'feature_usage', actionDetails: { view, feature: 'navigation' } });
  };
  useLoginSummary(handleViewChange);

  const renderContent = () => {
    switch (activeView) {
      case 'controlCenter':
        return <ControlCenter onNavigate={handleViewChange} />;
      case 'dashboard':
        // Until the role is known we cannot tell which home to show. Rendering the Today fallback meanwhile made
        // every role see the wrong screen for a moment (and fire its queries), so show a placeholder instead.
        if (roleLoading) return <HomeSkeleton />;
        switch (home.kind) {
          case 'controlTower': return <PMOCommandCenter onNavigate={handleViewChange} />;
          case 'executiveBrief': return <ExecutiveBrief onNavigate={handleViewChange} />;
          case 'projectCockpit': return <ProjectCockpit onNavigate={handleViewChange} />;
          case 'programBoard': return <ProgramBoard onNavigate={handleViewChange} />;
          case 'myDay': return <MyDay onNavigate={handleViewChange} />;
          case 'workspaceHealth': return <WorkspaceHealth onNavigate={handleViewChange} />;
          case 'controlCenter': return <ControlCenter onNavigate={handleViewChange} />;
          default: return <TodayView onNavigate={handleViewChange} />;
        }
      case 'overview':
        // The original widget dashboard (Executive Dashboard for the executive role)
        return (
          <ProtectedFeature feature="overview">
            {isExecutive ? <ExecutiveDashboard /> : <Dashboard onNavigateToSignals={() => setActiveView('hitlWorkbench')} />}
          </ProtectedFeature>
        );
      case 'deliverHub':
        return (
          <ProtectedFeature feature="deliverHub">
            <DeliveryOverview onNavigate={handleViewChange} />
          </ProtectedFeature>
        );
      case 'commHub':
        return (
          <ProtectedFeature feature="commHub">
            <CommunicationHub onNavigate={handleViewChange} />
          </ProtectedFeature>
        );
      case 'inbox':
        return <SmartInbox />;
      case 'actionHub':
        return (
          <ProtectedFeature feature="actionHub">
            <ActionHub onNavigateToGovernance={() => setActiveView('actionsRequired')} />
          </ProtectedFeature>
        );
      case 'calendar':
        return <CalendarView />;
      case 'meetings':
        return (
          <ProtectedFeature feature="meetings">
            <MeetingHub onNavigateToActionHub={() => setActiveView('actionHub')} />
          </ProtectedFeature>
        );
      case 'projects':
        return (
          <ProtectedFeature feature="projects">
            <ProjectsView />
          </ProtectedFeature>
        );
      case 'documents':
        return <DocumentsView onNavigateToDocumentIntelligence={() => setActiveView('documentIntelligence')} />;
      case 'documentIntelligence':
        return (
          <ProtectedFeature feature="documentIntelligence">
            <DocumentIntelligencePanel />
          </ProtectedFeature>
        );
      case 'stakeholders':
        return (
          <ProtectedFeature feature="stakeholders">
            <StakeholderView />
          </ProtectedFeature>
        );
      case 'reports':
        return (
          <ProtectedFeature feature="reports">
            <ReportsView />
          </ProtectedFeature>
        );
      case 'weeklyDigest':
        return (
          <ProtectedFeature feature="weeklyDigest">
            <WeeklyDigestView />
          </ProtectedFeature>
        );
      case 'portfolio':
        return (
          <ProtectedFeature feature="portfolio">
            <PortfolioView />
          </ProtectedFeature>
        );
      case 'compliance':
        return (
          <ProtectedFeature feature="compliance">
            <ComplianceCenter />
          </ProtectedFeature>
        );
      case 'activity':
        return (
          <ProtectedFeature feature="activity">
            <ActivityView />
          </ProtectedFeature>
        );
      case 'strategy':
        return (
          <ProtectedFeature feature="strategy">
            <StrategyView />
          </ProtectedFeature>
        );
      case 'team':
        return (
          <ProtectedFeature feature="team">
            <TeamView />
          </ProtectedFeature>
        );
      case 'knowledge':
        return (
          <ProtectedFeature feature="knowledge">
            <KnowledgeBase />
          </ProtectedFeature>
        );
      case 'branding':
        return (
          <ProtectedFeature feature="branding">
            <BrandingView />
          </ProtectedFeature>
        );
      case 'aiControls':
        return (
          <ProtectedFeature feature="aiControls">
            <AIControlsView />
          </ProtectedFeature>
        );
      case 'signals':
        return (
          <ProtectedFeature feature="signals">
            <SignalEngineView />
          </ProtectedFeature>
        );
      case 'risks':
        return (
          <ProtectedFeature feature="risks">
            <RiskPredictionView />
          </ProtectedFeature>
        );
      case 'dependencies':
        return (
          <ProtectedFeature feature="dependencies">
            <div className="space-y-6">
              <ProjectDependenciesSection />
              <ReleaseIntelligenceView />
            </div>
          </ProtectedFeature>
        );
      case 'actionsRequired':
        return (
          <ProtectedFeature feature="actionsRequired">
            <div className="space-y-6">
              <ActionsRequired />
              <IssueRegister />
            </div>
          </ProtectedFeature>
        );
      case 'decideBoard':
        return (
          <ProtectedFeature feature="decideBoard">
            <DecisionBoard onNavigate={handleViewChange} />
          </ProtectedFeature>
        );
      case 'traceability':
        return (
          <ProtectedFeature feature="traceability">
            <TraceabilityView onNavigate={handleViewChange} />
          </ProtectedFeature>
        );
      case 'decisions':
        return (
          <ProtectedFeature feature="decisions">
            <DecisionLog />
          </ProtectedFeature>
        );
      case 'auditTrail':
        return (
          <ProtectedFeature feature="auditTrail">
            <AuditTrailView />
          </ProtectedFeature>
        );
      case 'hitlWorkbench':
        return (
          <ProtectedFeature feature="hitlWorkbench">
            <HITLGovernanceView />
          </ProtectedFeature>
        );
      case 'analytics':
        return (
          <ProtectedFeature feature="analytics">
            <Suspense fallback={<div className="flex items-center justify-center py-24"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
              <AdminAnalytics embedded />
            </Suspense>
          </ProtectedFeature>
        );
      case 'tenantSettings':
        return (
          <ProtectedFeature feature="tenantSettings">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
              <TenantSettings />
            </Suspense>
          </ProtectedFeature>
        );
      case 'settings':
        return (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">{t('Settings')}</h2>
              <p>{t('Configuration options coming soon...')}</p>
            </div>
          </div>
        );
      default:
        return <Dashboard />;
    }
  };

  return (
    <ConsoleLayout activeView={activeView} onViewChange={handleViewChange}>
      <SectionTabs activeView={activeView} onViewChange={handleViewChange} />
      {renderContent()}
    </ConsoleLayout>
  );
};

export default Index;
